/**
 * Iter-229 — Booking Actions bottom sheet.
 * 13 user-listed actions; matches PopBookings-style action sheet.
 *
 * Iter-232b (2026-06-10) — Bugfix:
 *   Upload Photos / Submit Recap / Reimbursements used to route to
 *   `/talent/clock?tab=...` but ClockInOut ignores `?tab=`, so all 3
 *   actions visually opened the Clock In/Out screen. Now they deep-link
 *   into `/talent/booking/{id}?tab=photos|recap|reimbursements` where
 *   `BookingDetailsV2` renders the proper Upload-Photos, Post-Event-Recap,
 *   and Reimbursement-submission UIs.
 *   Release Gig now hits `POST /api/talent/bookings/{id}/release`.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Play, FileText, ListChecks, CalendarPlus, Info, Shirt, UserCheck,
  MessageSquare, DollarSign, Image as ImageIcon, Video, ClipboardCheck, Receipt, LogOut, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';

const Action = ({ icon: Icon, label, onClick, accent = false, testid }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1.5 rounded-xl p-3.5 text-center border
                ${accent
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-600 shadow-md'
                  : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'}`}
    data-testid={testid}
  >
    <Icon className={`w-5 h-5 ${accent ? '' : 'text-slate-700'}`} />
    <span className="text-[11px] font-semibold leading-tight">{label}</span>
  </button>
);

export default function BookingActionsSheet({ open, onClose, group, shift, onChange }) {
  const navigate = useNavigate();
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseNote, setReleaseNote] = useState('');
  if (!group) return null;

  const RELEASE_REASONS = [
    { code: 'illness', label: 'Illness' },
    { code: 'emergency', label: 'Emergency' },
    { code: 'transportation', label: 'Transportation problem' },
    { code: 'scheduling_conflict', label: 'Scheduling conflict' },
    { code: 'other', label: 'Other' },
  ];

  const today = new Date().toISOString().slice(0, 10);
  const isTodayShift = shift && shift.shift_date === today;

  // <24h release lockout — self-release is policy-locked; route to Evie.
  // Prefer the backend-authoritative release_state when present.
  const within24h = (() => {
    if (shift?.release_state) return shift.release_state === 'within_24h_locked';
    if (!shift?.shift_date) return false;
    const start = new Date(`${shift.shift_date}T${(shift.start_time || '09:00')}`);
    if (Number.isNaN(start.getTime())) return false;
    const diffH = (start.getTime() - Date.now()) / 3600000;
    return diffH > 0 && diffH < 24;
  })();

  const go = (path) => { onClose(); navigate(path); };

  const releaseGig = async () => {
    if (!shift?.id) return;
    if (!releaseReason) {
      toast.error('Please choose a reason for releasing this shift.');
      return;
    }
    setReleasing(true);
    try {
      await api.post(`/talent/bookings/${shift.id}/release`, {
        reason: releaseReason,
        note: releaseNote,
      });
      toast.success('Gig released — your admin and the client have been notified.');
      setReleaseOpen(false);
      setReleaseReason('');
      setReleaseNote('');
      onClose();
      onChange?.();
    } catch (e) {
      // Iter-370 — Backend now returns { code, message } for every
      // release refusal so the toast can name the exact reason.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'within_24h_contact_required') {
        setReleaseOpen(false);
        toast.message('Within 24 hours — chat with Evie to cancel', {
          description: 'This shift can no longer be self-released. Evie will report it and share next steps.',
          duration: 7000,
        });
        go('/talent/support');
        return;
      }
      const msg = (detail && typeof detail === 'object' && detail.message)
        ? detail.message
        : (typeof detail === 'string' ? detail : 'Failed to release gig');
      toast.error(msg, { duration: 8000 });
    } finally {
      setReleasing(false);
    }
  };

  // Calendar handler — for multi-day, generate ICS for ALL shifts in the series.
  const onAddToCal = () => {
    if (!group?.shifts?.length) return;
    const items = group.shifts.map((s, idx) => {
      const d = (s.shift_date || '').replace(/-/g, '');
      const start = `${d}T${(s.start_time || '09:00').replace(':', '')}00`;
      const end = `${d}T${(s.end_time || '17:00').replace(':', '')}00`;
      return [
        'BEGIN:VEVENT',
        `UID:ievntur-${s.id}@app.ievntur.com`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:${group.event_title}${group.shifts.length > 1 ? ` · Day ${idx + 1}` : ''}`,
        `LOCATION:${(group.venue || '')} ${(group.city || '')} ${(group.state || '')}`.trim(),
        `DESCRIPTION:Role: ${group.role || ''} · Pay: $${group.hourly_rate || '0'}/hr`,
        'END:VEVENT',
      ].join('\n');
    });
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//iEvntur//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
      ...items,
      'END:VCALENDAR',
    ].join('\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(group.event_title || 'booking').replace(/\W+/g, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Series added · ${group.shifts.length} shift${group.shifts.length > 1 ? 's' : ''}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 sm:max-w-md mx-auto rounded-t-3xl rounded-b-3xl border-0 shadow-2xl" data-testid="actions-sheet">
        <DialogTitle className="sr-only">Booking actions</DialogTitle>
        <div className="p-4 pb-6">
          <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4" />

          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 grid place-items-center text-white font-bold">
              {(group.client_name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-slate-900 truncate">{group.event_title}</div>
              <div className="text-xs text-slate-500 truncate">
                {shift ? `Day · ${formatDate(shift.shift_date)}` : `${group.total_shifts} shifts · ${group.role}`}
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center" aria-label="Close" data-testid="actions-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Action icon={Play}            label={isTodayShift ? 'Clock In / Out' : 'Check In · Day 1'} accent
                    onClick={() => shift && go(`/talent/clock?booking=${shift.id}`)} testid="action-clock" />
            <Action icon={FileText}        label="View Booking"        onClick={() => shift && go(`/talent/booking/${shift.id}`)} testid="action-view" />
            <Action icon={ListChecks}      label="Tasks"                onClick={() => shift && go(`/talent/booking/${shift.id}?tab=tasks`)} testid="action-tasks" />
            <Action icon={CalendarPlus}    label="Add to Calendar"      onClick={onAddToCal} testid="action-calendar" />
            <Action icon={Info}            label="Instructions"         onClick={() => shift && go(`/talent/booking/${shift.id}?tab=details#instructions`)} testid="action-instructions" />
            <Action icon={Shirt}           label="Dress Code"           onClick={() => shift && go(`/talent/booking/${shift.id}?tab=details#dress`)} testid="action-dress" />
            <Action icon={UserCheck}       label="Grooming"             onClick={() => shift && go(`/talent/booking/${shift.id}?tab=details#grooming`)} testid="action-grooming" />
            <Action icon={MessageSquare}   label="Message Admin"        onClick={() => shift && go(`/talent/support?booking=${shift.id}`)} testid="action-message" />
            <Action icon={DollarSign}      label="Payment Details"      onClick={() => go('/talent/earnings')} testid="action-payment" />
            <Action icon={ImageIcon}       label="Upload Photos"        onClick={() => shift && go(`/talent/booking/${shift.id}?tab=photos`)} testid="action-photos" />
            {/* Iter-253c — Video upload added per Mel's request. Routes to
                the same Booking Details media tab where the upload UI accepts
                both image/* and video/* file types. */}
            <Action icon={Video}           label="Upload Video"         onClick={() => shift && go(`/talent/booking/${shift.id}?tab=photos&type=video`)} testid="action-video" />
            <Action icon={ClipboardCheck}  label="Submit Recap"         onClick={() => shift && go(`/talent/booking/${shift.id}?tab=recap`)} testid="action-recap" />
            <Action icon={Receipt}         label="Reimbursements"       onClick={() => shift && go(`/talent/booking/${shift.id}?tab=reimbursements`)} testid="action-reimbursements" />
            {within24h ? (
              <div className="col-span-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-center" data-testid="release-locked-24h">
                <div className="text-xs font-bold text-amber-900 mb-1">Less than 24 hours to shift</div>
                <div className="text-[11px] text-amber-800 mb-2 leading-snug">
                  This shift begins in under 24 hours, so it can no longer be self-released
                  in the app. iEvntür needs immediate notice so a replacement can be found —
                  chat with Evie to report the cancellation and get next steps.
                </div>
                <button
                  onClick={() => shift && go('/talent/support')}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 text-xs font-bold"
                  data-testid="release-contact-evie"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Chat with Evie to Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setReleaseOpen(true)}
                className="col-span-2 flex items-center justify-center gap-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl py-3 text-xs font-semibold"
                data-testid="action-release"
              >
                <LogOut className="w-4 h-4" /> Release Gig
              </button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Release Gig — require a reason; offer a direct message path */}
      <AlertDialog open={releaseOpen} onOpenChange={(o) => !o && !releasing && setReleaseOpen(false)}>
        <AlertDialogContent data-testid="release-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Release this shift?</AlertDialogTitle>
            <AlertDialogDescription>
              Please tell us why. Your admin and the client are notified immediately so we can arrange coverage. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-1">
            <div className="text-xs font-semibold text-slate-700">Reason for release</div>
            <div className="grid grid-cols-1 gap-1.5">
              {RELEASE_REASONS.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => setReleaseReason(r.code)}
                  className={`text-left rounded-lg border px-3 py-2 text-sm transition-colors
                    ${releaseReason === r.code
                      ? 'border-cyan-600 bg-cyan-50 text-cyan-800 font-semibold'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`release-reason-${r.code}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <textarea
              value={releaseNote}
              onChange={(e) => setReleaseNote(e.target.value)}
              placeholder="Add a short note for our team (optional)"
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              data-testid="release-note-input"
            />
            <button
              type="button"
              onClick={() => { setReleaseOpen(false); go(`/talent/support?booking=${shift.id}`); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-700 py-2 text-xs font-semibold"
              data-testid="release-message-admin"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Need to reach us instead? Message Admin
            </button>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={releasing}>Keep shift</AlertDialogCancel>
            <AlertDialogAction
              onClick={releaseGig}
              disabled={releasing || !releaseReason}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="release-confirm-btn"
            >
              {releasing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Release shift
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
